import { timeoutDefaultValue } from '@gik/core/constants';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { moveCursorToEnd } from '@gik/core/utils/cursor';
import he from 'he';
import React from 'react';
import type { OnChangeHandlerFunc, SuggestionDataItem } from 'react-mentions';
import { Mention, MentionsInput } from 'react-mentions';
import type { UIVariant } from '../types';

export type InputMentionsProps = {
  value?: string;
  placeholder?: string;
  variant?: UIVariant;
  singleLine?: boolean;
  disabled?: boolean;
  submitOnEnter?: boolean;
  cancelOnEscape?: boolean;
  padding?: boolean;
  appendSpaceOnAdd?: boolean;
  id?: string;
  autoFocus?: boolean;
  users?: SuggestionDataItem[];
  onChange?: OnChangeHandlerFunc;
  onCancel?: () => void;
  onSubmit?: (value: string) => void;
} & UIComponent;

export function InputMentions({
  autoFocus,
  className,
  placeholder,
  value,
  variant,
  onChange,
  onCancel,
  onSubmit,
  singleLine,
  disabled,
  appendSpaceOnAdd = true,
  submitOnEnter,
  cancelOnEscape,
  id,
  users,
  padding,
}: InputMentionsProps): React.ReactElement {
  const bem = useBemCN('input-mentions');

  const mainRef = React.useRef();

  React.useEffect(() => {
    if (autoFocus) {
      const inputEl = document.getElementById(id) as HTMLTextAreaElement;
      setTimeout(() => {
        if (inputEl) moveCursorToEnd(inputEl);
      }, timeoutDefaultValue);
    }
  }, [autoFocus, id]);

  function renderUserSuggestion(entry: SuggestionDataItem, search: string, highlightedDisplay: string) {
    return (
      <a
        // onClick={() => UI.alert(`you clicked user ${entry.display}`)}
        {...bem('user')}
      >
        {highlightedDisplay}
      </a>
    );
  }

  return (
    <MentionsInput
      id={id}
      value={value ? he.decode(value) : value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={onChange}
      {...bem(null, [{ [`variant-${variant}`]: variant }, { padding }, { disabled }], className)}
      singleLine={singleLine}
      onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (submitOnEnter && event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          event.stopPropagation();
          onSubmit?.(value);
          return false;
        }
        if (cancelOnEscape && event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          onCancel?.();
          return false;
        }
        return true;
      }}
    >
      <Mention
        trigger="@"
        data={users || []}
        appendSpaceOnAdd={appendSpaceOnAdd}
        renderSuggestion={renderUserSuggestion}
        displayTransform={(id: string, display: string) => {
          return `@${display || id}`;
        }}
      />
    </MentionsInput>
  );
}
